import { Controller } from 'stimulus'

// Icon SVG strings — mirror DictionaryHelper#legal_characters_chips_icon_* (needed for dynamically added chips).
const CHIP_ACTION_ICON_COPY = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5"/><path d="M10.5 5.5V3.5A1.5 1.5 0 0 0 9 2H3.5A1.5 1.5 0 0 0 2 3.5V9a1.5 1.5 0 0 0 1.5 1.5h2"/></svg>'
const CHIP_ACTION_ICON_SUCCESS = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5"/></svg>'
const CHIP_ACTION_ICON_REMOVE = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>'

// Row grouping tolerance - chips at similar Y positions (within 4px) are considered on the same row
const DRAG_ROW_Y_TOLERANCE_PX = 4

// Drag threshold - mouse must move at least 5px before drag starts (prevents accidental drags on clicks)
const DRAG_START_THRESHOLD_PX = 5

const CHIP_COPY_FEEDBACK_MS = 1200

export default class extends Controller {
  static targets = [
    'ottomanChipList',
    'ottomanInput',
    'ottomanHidden',
    'ottomanCount',
    'latinChipList',
    'latinInput',
    'latinHidden',
    'latinCount',
    'toast',
  ]

  connect() {
    // Verify required global method from global_methods.js is available
    if (typeof window.escapeHtmlAttribute !== 'function') {
      console.error('legal_characters_chips_controller requires window.escapeHtmlAttribute from global_methods.js')
      return
    }

    // Bind handlers to preserve 'this' context so we can remove them later in disconnect
    this.onChipGridPointerDown = this.onChipGridPointerDown.bind(this)
    this.onChipGridKeydownReorder = this.onChipGridKeydownReorder.bind(this)
    this.onDocumentPointerMoveDuringChipDrag = this.onDocumentPointerMoveDuringChipDrag.bind(this)
    this.onDocumentPointerUpDuringChipDrag = this.onDocumentPointerUpDuringChipDrag.bind(this)
    this.onDocumentPointerCancelDuringChipDrag = this.onDocumentPointerCancelDuringChipDrag.bind(this)
    this.onDocumentEscapeDuringChipDrag = this.onDocumentEscapeDuringChipDrag.bind(this)
    this.handleChipPointerOut = this.handleChipPointerOut.bind(this)

    // Setup drag and keyboard reordering for both Ottoman and Latin character grids
    this.chipGridElements = [this.ottomanChipListTarget, this.latinChipListTarget]
    this.chipGridElements.forEach((chipListGrid) => {
      chipListGrid.addEventListener('pointerdown', this.onChipGridPointerDown)
      chipListGrid.addEventListener('keydown', this.onChipGridKeydownReorder)
      chipListGrid.addEventListener('pointerout', this.handleChipPointerOut)
    })

    // Move toast notification element to body so it stays visible when user scrolls the form
    if (this.hasToastTarget) {
      this.copyToastElement = this.toastTarget
      if (this.copyToastElement.parentElement !== document.body) {
        document.body.appendChild(this.copyToastElement)
      }
    }
  }

  disconnect() {
    this.finishChipDrag(false) // Clean up any ongoing drag operation
    this.chipGridElements?.forEach((chipListGrid) => {
      chipListGrid.removeEventListener('pointerdown', this.onChipGridPointerDown)
      chipListGrid.removeEventListener('keydown', this.onChipGridKeydownReorder)
      chipListGrid.removeEventListener('pointerout', this.handleChipPointerOut)
    })
    this.clearCopySuccessFeedback()
    clearTimeout(this.copyAllSuccessTimeoutId)
    clearTimeout(this.copyToastHideTimeoutId)
    if (this.copyToastElement?.parentElement === document.body) {
      this.copyToastElement.remove()
    }
    this.copyToastElement = null
  }

  // Display temporary feedback message at bottom of screen (e.g., "Copied ک") that auto-hides after durationMs
  showCopyFeedbackToast(html, durationMs = 1800) {
    const toast = this.copyToastElement
    if (!toast) return
    toast.innerHTML = `<span>${html}</span>`
    toast.classList.remove('legal-characters-chips-copy-toast--visible')
    requestAnimationFrame(() => {
      toast.classList.add('legal-characters-chips-copy-toast--visible')
    })
    clearTimeout(this.copyToastHideTimeoutId)
    this.copyToastHideTimeoutId = setTimeout(
      () => toast.classList.remove('legal-characters-chips-copy-toast--visible'),
      durationMs,
    )
  }

  // Extract the actual character value from chip's data-legal-characters-chips-value attribute
  chipCharacterValue(chipElement) {
    return chipElement.getAttribute('data-legal-characters-chips-value') ?? ''
  }

  // Matches ERB: U+#{'%04X' % character.codepoints.first}
  formatUnicodeCodePoint(character) {
    if (!character) return 'U+????'
    const codePoint = character.codePointAt(0)
    if (codePoint == null) return 'U+????'
    return `U+${codePoint.toString(16).toUpperCase().padStart(4, '0')}`
  }

  // Character, tab, then Unicode label — pastes as two columns in Excel.
  tabSeparatedCharacterLine(character) {
    return `${character}\t${this.formatUnicodeCodePoint(character)}`
  }

  // Dynamic chips use window.escapeHtmlAttribute (global_methods.js), same as flash message attributes.
  buildChipMarkup(character, isOttomanSet) {
    const escapedCharacter = window.escapeHtmlAttribute(character)
    const unicodeLabel = this.formatUnicodeCodePoint(character)
    const glyphSetClass = isOttomanSet
      ? ' legal-characters-chips-chip-glyph-ottoman'
      : ' legal-characters-chips-chip-glyph-latin'
    const textDirection = isOttomanSet ? ' dir="rtl"' : ''
    return `<span class="legal-characters-chips-chip" data-legal-characters-chips-value="${escapedCharacter}" data-legal-characters-chips-codepoint="${window.escapeHtmlAttribute(unicodeLabel)}" tabindex="0" role="listitem" aria-label="${window.escapeHtmlAttribute(`${character} ${unicodeLabel}`)}">
      <span class="legal-characters-chips-chip-glyph text-center${glyphSetClass}"${textDirection}>${escapedCharacter}</span>
      <div class="legal-characters-chips-chip-toolbar">
        <button type="button" class="legal-characters-chips-chip-btn legal-characters-chips-chip-btn-copy" tabindex="-1" aria-label="Copy" data-action="click->legal-characters-chips#copySingleCharacterFromChip:stop">${CHIP_ACTION_ICON_COPY}</button>
        <button type="button" class="legal-characters-chips-chip-btn legal-characters-chips-chip-btn-remove" tabindex="-1" aria-label="Remove" data-action="legal-characters-chips#removeCharacterChip">${CHIP_ACTION_ICON_REMOVE}</button>
      </div>
      <button type="button" class="legal-characters-chips-chip-codepoint" tabindex="-1" aria-label="Copy code" data-action="click->legal-characters-chips#copyUnicodeCodeFromChip:stop">${window.escapeHtmlAttribute(unicodeLabel)}</button>
    </span>`
  }

  // Unicode label from chip markup (ERB / buildChipMarkup); fallback for legacy nodes
  chipUnicodeCodePointLabel(chipElement) {
    return (
      chipElement.dataset.legalCharactersChipsCodepoint ??
      this.formatUnicodeCodePoint(this.chipCharacterValue(chipElement))
    )
  }

  // Check user's OS accessibility setting for reduced motion preference
  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  // Update hidden form field with current chip order and update the "N characters" count label
  syncHiddenFieldAndCountFromGrid(chipListGrid, field) {
    const hiddenInput =
      field === 'ottoman' ? this.ottomanHiddenTarget : this.latinHiddenTarget
    const countElement =
      field === 'ottoman' ? this.ottomanCountTarget : this.latinCountTarget
    const chipElements = [...chipListGrid.querySelectorAll('[data-legal-characters-chips-value]')]
    hiddenInput.value = chipElements.map((el) => this.chipCharacterValue(el)).join(', ')
    countElement.textContent = `${chipElements.length} characters`
  }

  // Unlike ApplicationController#copyToClipboard (writeText only), returns boolean for bottom toast
  // and uses execCommand fallback when navigator.clipboard fails (permissions / older browsers).
  async copyTextToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Fallback for older browsers or when clipboard permission denied
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.cssText = 'position:fixed;opacity:0'
      document.body.appendChild(textarea)
      textarea.select()
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch (_) {
        /* ignore */
      }
      textarea.remove()
      return ok
    }
  }

  // Collapse chip actions (hide copy/code buttons) when pointer leaves, unless moving to a child element
  handleChipPointerOut(event) {
    const chipElement = event.target.closest('[data-legal-characters-chips-value]')
    if (!chipElement) return
    const related = event.relatedTarget
    if (related instanceof Node && chipElement.contains(related)) return
    chipElement.classList.remove('legal-characters-chips-chip--actions-hidden')
  }

  // Reset copy button to default state (copy icon, no success modifier)
  resetChipCopyActButton(copyButton) {
    copyButton.innerHTML = CHIP_ACTION_ICON_COPY
    copyButton.classList.remove('legal-characters-chips-chip-btn--success')
  }

  // Reset unicode code button to show the unicode label (e.g., "U+0641")
  resetUnicodeCodeButton(codeButton, chipElement) {
    codeButton.classList.remove('legal-characters-chips-chip-codepoint--success')
    codeButton.textContent = this.chipUnicodeCodePointLabel(chipElement)
  }

  // Reset chip hover UI: restore buttons to default state, remove focus, and collapse action buttons
  collapseChipActionsAndResetButtons(chipElement, { codeButton = null, copyButton = null } = {}) {
    const copyBtn = copyButton || chipElement.querySelector('.legal-characters-chips-chip-btn-copy')
    const codeBtn = codeButton || chipElement.querySelector('.legal-characters-chips-chip-codepoint')
    if (copyBtn) this.resetChipCopyActButton(copyBtn)
    if (codeBtn) this.resetUnicodeCodeButton(codeBtn, chipElement)
    codeBtn?.blur()
    copyBtn?.blur()
    if (chipElement.contains(document.activeElement)) {
      chipElement.blur()
    }
    chipElement.classList.remove('legal-characters-chips-chip--copy-feedback')
    chipElement.classList.add('legal-characters-chips-chip--actions-hidden')
  }

  // Clear the chip copy feedback timer and reset any chip showing "copied" checkmark feedback
  clearCopySuccessFeedback() {
    if (this.copyFeedbackTimeoutId) {
      clearTimeout(this.copyFeedbackTimeoutId)
      this.copyFeedbackTimeoutId = null
    }
    if (!this.copyFeedbackChipState) return
    const { chipElement, codeButton, copyButton } = this.copyFeedbackChipState
    this.collapseChipActionsAndResetButtons(chipElement, { codeButton, copyButton })
    this.copyFeedbackChipState = null
  }

  // Show checkmark feedback on copy/code button for CHIP_COPY_FEEDBACK_MS, then auto-reset to original state
  showCopySuccessOnChip({ chipElement, copyButton = null, codeButton = null }) {
    if (
      this.copyFeedbackChipState?.chipElement &&
      this.copyFeedbackChipState.chipElement !== chipElement
    ) {
      const prev = this.copyFeedbackChipState
      this.collapseChipActionsAndResetButtons(prev.chipElement, {
        codeButton: prev.codeButton,
        copyButton: prev.copyButton,
      })
    }

    if (this.copyFeedbackTimeoutId) {
      clearTimeout(this.copyFeedbackTimeoutId)
      this.copyFeedbackTimeoutId = null
    }

    chipElement.classList.remove('legal-characters-chips-chip--actions-hidden')
    chipElement.classList.add('legal-characters-chips-chip--copy-feedback')

    const label = this.chipUnicodeCodePointLabel(chipElement)
    const copyAct = chipElement.querySelector('.legal-characters-chips-chip-btn-copy')
    const codeAct = chipElement.querySelector('.legal-characters-chips-chip-codepoint')

    // Only update the button that was actually clicked
    if (copyButton) {
      copyAct.innerHTML = CHIP_ACTION_ICON_SUCCESS
      copyAct.classList.add('legal-characters-chips-chip-btn--success')
    }
    if (codeButton) {
      codeAct.classList.add('legal-characters-chips-chip-codepoint--success')
      codeAct.innerHTML = `${CHIP_ACTION_ICON_SUCCESS}${label}`
    }

    this.copyFeedbackChipState = {
      chipElement,
      codeButton: codeButton ? codeAct : null,
      copyButton: copyButton ? copyAct : null,
    }
    this.copyFeedbackTimeoutId = setTimeout(() => {
      const state = this.copyFeedbackChipState
      this.copyFeedbackTimeoutId = null
      this.copyFeedbackChipState = null
      if (!state) return
      this.collapseChipActionsAndResetButtons(state.chipElement, {
        codeButton: state.codeButton,
        copyButton: state.copyButton,
      })
    }, CHIP_COPY_FEEDBACK_MS)
  }

  // Shared copy-from-chip handler (character line vs unicode code); Stimulus actions stay separate entry points
  async copyFromChipButton(event, mode) {
    event.stopPropagation()
    event.preventDefault()
    
    // Capture event.currentTarget immediately before async operations
    // (event.currentTarget becomes null after async operations complete)
    const clickedButton = event.currentTarget
    
    const chipElement = clickedButton.closest('[data-legal-characters-chips-value]')
    if (!chipElement) return

    const character = this.chipCharacterValue(chipElement)
    if (!character) return

    const unicodeLabel = this.chipUnicodeCodePointLabel(chipElement)
    const clipboardText =
      mode === 'code' ? unicodeLabel : this.tabSeparatedCharacterLine(character)
    const ok = await this.copyTextToClipboard(clipboardText)
    if (ok) {
      this.showCopySuccessOnChip({
        chipElement,
        copyButton: mode === 'line' ? clickedButton : null,
        codeButton: mode === 'code' ? clickedButton : null,
      })
      const toastHtml =
        mode === 'code'
          ? `Copied ${unicodeLabel}`
          : `Copied <span class="legal-characters-chips-copy-toast-glyph">${window.escapeHtmlAttribute(character)}</span> ${unicodeLabel}`
      this.showCopyFeedbackToast(toastHtml)
    } else {
      this.showCopyFeedbackToast('Copy failed. Your browser blocked clipboard access.')
    }
  }

  // Copy single character to clipboard and show checkmark feedback on copy button, then toast notification
  async copySingleCharacterFromChip(event) {
    return this.copyFromChipButton(event, 'line')
  }

  // Copy unicode code point (e.g., "U+0641") to clipboard and show checkmark feedback on code button
  async copyUnicodeCodeFromChip(event) {
    return this.copyFromChipButton(event, 'code')
  }

  // Copy all characters in a set (Ottoman or Latin) with tab-separated codes, one per line
  async copyAllCharactersInSet(event) {
    event.preventDefault()
    const characterSet = event.currentTarget.dataset.field
    if (!characterSet) return

    const chipListGrid =
      characterSet === 'ottoman' ? this.ottomanChipListTarget : this.latinChipListTarget
    const copyAllButton = event.currentTarget
    const charactersInOrder = [...chipListGrid.querySelectorAll('[data-legal-characters-chips-value]')].map(
      (el) => this.chipCharacterValue(el),
    )
    const lines = charactersInOrder.map((c) => this.tabSeparatedCharacterLine(c))
    const ok = await this.copyTextToClipboard(lines.join('\r\n'))
    const characterSetTitle = characterSet.charAt(0).toUpperCase() + characterSet.slice(1)

    if (ok) {
      copyAllButton.classList.add('legal-characters-chips-copy-set--success')
      clearTimeout(this.copyAllSuccessTimeoutId)
      this.copyAllSuccessTimeoutId = setTimeout(
        () => copyAllButton.classList.remove('legal-characters-chips-copy-set--success'),
        CHIP_COPY_FEEDBACK_MS,
      )
      this.showCopyFeedbackToast(
        `Copied all ${charactersInOrder.length} ${characterSetTitle} characters with their codes, one per line`,
      )
    } else {
      this.showCopyFeedbackToast('Copy failed. Your browser blocked clipboard access.')
    }
  }

  // Add characters from input field as chips - skips duplicates and whitespace, shows message if only spaces entered
  addCharactersFromInput(event) {
    event?.preventDefault()
    const characterSet = event.currentTarget.dataset.field
    if (!characterSet) return

    const isOttomanSet = characterSet === 'ottoman'
    const chipListGrid = isOttomanSet ? this.ottomanChipListTarget : this.latinChipListTarget
    const characterInput = isOttomanSet ? this.ottomanInputTarget : this.latinInputTarget

    const inputText = characterInput.value
    if (!inputText) return

    const inputIsOnlyWhitespace = [...inputText].every((c) => c.trim() === '')
    let newCharactersAddedCount = 0

    // Process each character: skip whitespace, check for duplicates, build and insert chip HTML
    for (const character of Array.from(inputText)) {
      if (character.trim() === '') continue
      const isCharacterAlreadyInGrid = [...chipListGrid.querySelectorAll('[data-legal-characters-chips-value]')].some(
        (chipElement) => this.chipCharacterValue(chipElement) === character,
      )

      if (!isCharacterAlreadyInGrid) {
        chipListGrid.insertAdjacentHTML(
          'beforeend',
          this.buildChipMarkup(character, isOttomanSet),
        )
        newCharactersAddedCount += 1
      }
    }

    // If user only typed spaces, explain why nothing was added (spaces handled by separate "Allow space" setting)
    if (newCharactersAddedCount === 0 && inputIsOnlyWhitespace) {
      this.showCopyFeedbackToast(
        'Space and whitespace are not added here. Use Allow space settings below.',
      )
    }

    this.syncHiddenFieldAndCountFromGrid(chipListGrid, characterSet)
    characterInput.value = ''
    characterInput.focus()
  }

  // Remove chip from grid and update form when user clicks X button
  removeCharacterChip(event) {
    event.preventDefault()
    event.stopPropagation()
    const chipElement = event.currentTarget.closest('[data-legal-characters-chips-value]')
    if (!chipElement) return

    const chipListGrid = chipElement.closest('.legal-characters-chips-grid')
    const characterSet = chipListGrid?.dataset.field
    if (!characterSet) return

    const characterInput =
      characterSet === 'ottoman' ? this.ottomanInputTarget : this.latinInputTarget
    chipElement.remove()
    this.syncHiddenFieldAndCountFromGrid(chipListGrid, characterSet)
    characterInput.focus()
  }

  // Allow pressing Enter in input field as alternative to clicking Add button
  addCharactersOnEnterKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.addCharactersFromInput(event)
    }
  }

  // Keyboard reordering: Ctrl+Left/Right arrows move focused chip one position (accessibility feature)
  onChipGridKeydownReorder(event) {
    if (!event.ctrlKey) return
    const chipElement = event.target.closest('[data-legal-characters-chips-value]')
    if (!chipElement || document.activeElement !== chipElement) return

    const chipListGrid = chipElement.parentElement
    const chips = [...chipListGrid.children]
    const index = chips.indexOf(chipElement)
    if (index < 0) return

    let newIndex = index
    if (event.key === 'ArrowLeft') newIndex = index - 1
    else if (event.key === 'ArrowRight') newIndex = index + 1
    else return

    if (newIndex < 0 || newIndex >= chips.length) return

    event.preventDefault()
    const neighborChip = chips[newIndex]
    this.reorderGridWithFlipAnimation(
      chipListGrid,
      () => {
        if (newIndex < index) {
          chipListGrid.insertBefore(chipElement, neighborChip)
        } else {
          chipListGrid.insertBefore(chipElement, neighborChip.nextElementSibling)
        }
      },
      chipElement,
    )
    this.syncHiddenFieldAndCountFromGrid(chipListGrid, chipListGrid.dataset.field)
    chipElement.focus({ preventScroll: true })
  }

  // Initialize drag tracking when user presses on chip (not on action buttons)
  onChipGridPointerDown(event) {
    if (event.target.closest('.legal-characters-chips-chip-btn, .legal-characters-chips-chip-codepoint')) {
      event.stopPropagation()
      return
    }
    if (event.button !== 0) return

    const draggedChip = event.target.closest('[data-legal-characters-chips-value]')
    if (!draggedChip) return

    const chipListGrid = draggedChip.parentElement
    this.activeChipDrag = {
      characterSet: chipListGrid.dataset.field,
      draggedChip,
      chipListGrid,
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      isDragging: false,
      pointerId: event.pointerId,
      originalChipIndex: [...chipListGrid.children].indexOf(draggedChip),
    }

    document.addEventListener('pointermove', this.onDocumentPointerMoveDuringChipDrag)
    document.addEventListener('pointerup', this.onDocumentPointerUpDuringChipDrag)
    document.addEventListener('pointercancel', this.onDocumentPointerCancelDuringChipDrag)
    document.addEventListener('keydown', this.onDocumentEscapeDuringChipDrag)
  }

  // Position ghost chip element to follow mouse cursor at specified scale
  moveDragGhostToPointer(event, scale = 1.06) {
    const { dragGhostElement, ghostOffsetX, ghostOffsetY } = this.activeChipDrag
    dragGhostElement.style.transform = `translate(${event.clientX - ghostOffsetX}px, ${event.clientY - ghostOffsetY}px) scale(${scale})`
  }

  // Handle mouse movement: activate drag if moved enough, update ghost position, calculate insertion point
  onDocumentPointerMoveDuringChipDrag(event) {
    if (!this.activeChipDrag || event.pointerId !== this.activeChipDrag.pointerId) return

    if (!this.activeChipDrag.isDragging) {
      const distance = Math.hypot(
        event.clientX - this.activeChipDrag.pointerStartX,
        event.clientY - this.activeChipDrag.pointerStartY,
      )
      if (distance < DRAG_START_THRESHOLD_PX) return
      
      // Activate drag: create floating ghost copy and make original transparent placeholder
      const { draggedChip } = this.activeChipDrag
      const rect = draggedChip.getBoundingClientRect()
      this.activeChipDrag.ghostOffsetX = this.activeChipDrag.pointerStartX - rect.left
      this.activeChipDrag.ghostOffsetY = this.activeChipDrag.pointerStartY - rect.top

      const dragGhostElement = draggedChip.cloneNode(true)
      dragGhostElement.classList.add('legal-characters-chips-drag-ghost')
      dragGhostElement.style.width = `${rect.width}px`
      dragGhostElement.style.height = `${rect.height}px`
      document.body.appendChild(dragGhostElement)
      this.activeChipDrag.dragGhostElement = dragGhostElement

      if (this.copyFeedbackChipState?.chipElement === draggedChip) {
        this.clearCopySuccessFeedback()
      } else {
        this.collapseChipActionsAndResetButtons(draggedChip)
      }
      draggedChip.classList.add('legal-characters-chips-chip--drag-placeholder')
      document.body.classList.add('legal-characters-chips-dragging')
      this.activeChipDrag.isDragging = true
      this.moveDragGhostToPointer(event, 1.06)
    }

    event.preventDefault()
    this.moveDragGhostToPointer(event)
    this.reorderDraggedChipAtPointer(event.clientX, event.clientY)
  }

  // Calculate chip insertion position based on pointer location - handles multi-row layouts intelligently
  reorderDraggedChipAtPointer(pointerX, pointerY) {
    const { chipListGrid, draggedChip } = this.activeChipDrag
    const gridRect = chipListGrid.getBoundingClientRect()
    const x = pointerX - gridRect.left
    const y = pointerY - gridRect.top
    const siblingChips = [...chipListGrid.children].filter((n) => n !== draggedChip)
    if (!siblingChips.length) return

    // Group chips into rows based on offsetTop (within tolerance)
    const chipRowsByOffsetTop = []
    siblingChips.forEach((n) => {
      const rowTop = n.offsetTop
      let row = chipRowsByOffsetTop.find(
        (r) => Math.abs(r.top - rowTop) < DRAG_ROW_Y_TOLERANCE_PX,
      )
      if (!row) {
        row = { top: rowTop, h: n.offsetHeight, items: [] }
        chipRowsByOffsetTop.push(row)
      }
      row.items.push(n)
    })

    const placeholderRowTop = draggedChip.offsetTop
    if (
      !chipRowsByOffsetTop.find((r) => Math.abs(r.top - placeholderRowTop) < DRAG_ROW_Y_TOLERANCE_PX)
    ) {
      chipRowsByOffsetTop.push({ top: placeholderRowTop, h: draggedChip.offsetHeight, items: [] })
    }
    chipRowsByOffsetTop.sort((a, b) => a.top - b.top)

    // Find which row pointer is over (or closest to)
    let row =
      chipRowsByOffsetTop.find(
        (r) =>
          y >= r.top - DRAG_ROW_Y_TOLERANCE_PX &&
          y <= r.top + r.h + DRAG_ROW_Y_TOLERANCE_PX,
      ) ||
      chipRowsByOffsetTop.reduce((a, b) =>
        Math.abs(y - (a.top + a.h / 2)) < Math.abs(y - (b.top + b.h / 2)) ? a : b,
      )

    if (!row.items.length) return

    // Find target chip in row: insert before if pointer on left half, after if on right half
    let targetChip = null
    let insertBeforeTarget = true
    for (const n of row.items) {
      const mid = n.offsetLeft + n.offsetWidth / 2
      if (x < mid) {
        targetChip = n
        insertBeforeTarget = true
        break
      }
    }
    if (!targetChip) {
      targetChip = row.items[row.items.length - 1]
      insertBeforeTarget = false
    }

    const insertBeforeReference = insertBeforeTarget ? targetChip : targetChip.nextElementSibling
    // Avoid unnecessary moves if chip already in correct position
    if (
      insertBeforeReference === draggedChip ||
      (insertBeforeTarget && targetChip.previousElementSibling === draggedChip) ||
      (!insertBeforeTarget && targetChip.nextElementSibling === draggedChip)
    ) {
      return
    }

    this.reorderGridWithFlipAnimation(chipListGrid, () => chipListGrid.insertBefore(draggedChip, insertBeforeReference))
  }

  // Animate chip reordering using FLIP technique (First Last Invert Play) - skips if user prefers reduced motion
  reorderGridWithFlipAnimation(chipListGrid, domReorderCallback, chipToSkipInFlipAnimation = null) {
    const kids = [...chipListGrid.children]
    const positionsBefore = new Map(kids.map((n) => [n, { x: n.offsetLeft, y: n.offsetTop }]))
    domReorderCallback()
    if (this.prefersReducedMotion) return

    const skip = chipToSkipInFlipAnimation ?? this.activeChipDrag?.draggedChip
    kids.forEach((n) => {
      if (n === skip) return
      // Safety check: skip if element was removed from DOM during reorder
      if (!n.parentElement) return
      const before = positionsBefore.get(n)
      const dx = before.x - n.offsetLeft
      const dy = before.y - n.offsetTop
      if (!dx && !dy) return
      n.getAnimations().forEach((an) => an.cancel())
      n.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
        { duration: 180, easing: 'cubic-bezier(.2,.7,.3,1)' },
      )
    })
  }

  // Finish drag operation: remove ghost, restore chip, save order (or revert if canceled)
  finishChipDrag(shouldCommitOrder) {
    document.removeEventListener('pointermove', this.onDocumentPointerMoveDuringChipDrag)
    document.removeEventListener('pointerup', this.onDocumentPointerUpDuringChipDrag)
    document.removeEventListener('pointercancel', this.onDocumentPointerCancelDuringChipDrag)
    document.removeEventListener('keydown', this.onDocumentEscapeDuringChipDrag)

    if (!this.activeChipDrag) return
    const dragState = this.activeChipDrag
    this.activeChipDrag = null

    if (!dragState.isDragging) return

    // Revert chip to original position if drag canceled (Escape key or pointer cancel)
    if (!shouldCommitOrder) {
      const siblings = [...dragState.chipListGrid.children].filter((n) => n !== dragState.draggedChip)
      dragState.chipListGrid.insertBefore(
        dragState.draggedChip,
        siblings[dragState.originalChipIndex] || null,
      )
    } else {
      this.syncHiddenFieldAndCountFromGrid(dragState.chipListGrid, dragState.characterSet)
    }

    const removeDragGhostAndRestoreChip = () => {
      dragState.dragGhostElement.remove()
      const chip = dragState.draggedChip
      chip.classList.remove('legal-characters-chips-chip--drag-placeholder')
      document.body.classList.remove('legal-characters-chips-dragging')
      if (this.copyFeedbackChipState?.chipElement === chip) {
        this.clearCopySuccessFeedback()
      } else {
        this.collapseChipActionsAndResetButtons(chip)
      }
    }

    const chipRect = dragState.draggedChip.getBoundingClientRect()
    if (this.prefersReducedMotion) {
      removeDragGhostAndRestoreChip()
      return
    }

    // Animate ghost flying back to chip's final position before cleanup
    const animation = dragState.dragGhostElement.animate(
      [
        { transform: dragState.dragGhostElement.style.transform },
        { transform: `translate(${chipRect.left}px, ${chipRect.top}px) scale(1)` },
      ],
      { duration: 150, easing: 'ease-out', fill: 'forwards' },
    )
    animation.finished.then(removeDragGhostAndRestoreChip).catch(removeDragGhostAndRestoreChip)
  }

  onDocumentPointerUpDuringChipDrag(event) {
    if (this.activeChipDrag && event.pointerId === this.activeChipDrag.pointerId) {
      this.finishChipDrag(true) // Commit the new chip order
    }
  }

  onDocumentPointerCancelDuringChipDrag() {
    this.finishChipDrag(false) // Revert to original position
  }

  onDocumentEscapeDuringChipDrag(event) {
    if (event.key === 'Escape' && this.activeChipDrag?.isDragging) {
      event.preventDefault()
      this.finishChipDrag(false) // Cancel drag and revert
    }
  }
}
